/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useState } from "react";
import produce from "immer";
import fetchHelper from "../../services/fetchHelper";
import { LayoutContext } from "../layout";
import { useToasts } from "react-toast-notifications";

export const PermissionItem = ({ title, value, name, onChange }) => {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "15px 0" }}>
      <span style={{ marginRight: "5px", fontSize: "14px", width: "200px" }}>{title}</span>
      <div style={{ display: "flex" }}>
        <label className="formLi flex0" style={{ display: "flex", alignItems: "center" }}>
          <input
            type="radio"
            name={name}
            data-key={name}
            className="chk"
            value={1}
            onChange={onChange}
            checked={value == true}
          />
          <span className="chkmrk radio" style={{ margin: "0 5px" }} />
          <span className="chk-label">YES</span>
        </label>
        <label className="formLi flex0" style={{ display: "flex", alignItems: "center" }}>
          <input
            type="radio"
            name={name}
            data-key={name}
            className="chk"
            value={0}
            onChange={onChange}
            checked={value == false}
          />
          <span className="chkmrk radio" style={{ margin: "0 5px" }} />
          <span className="chk-label">NO</span>
        </label>
      </div>
    </div>
  );
};

const UserModal = ({
  title = "Add User",
  editMode = true,
  modalId = "editUser",
  userObject,
  setUserObject,
  fetchData,
}) => {
  const { user } = useContext(LayoutContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [error, setError] = useState({});
  const { addToast } = useToasts();

  const handleOnChangeV2 = (e) => {
    const { name, value } = e.target;
    setError((prev) => {
      delete prev[name];
      return prev;
    });

    setUserObject((prev) =>
      produce(prev, (draft) => {
        draft[name] = value;
      })
    );
  };

  // ⚠️ DEPRECATED: This method is part of the legacy codebase and should NOT be used. Use handleOnChangeV2 method as alternative.

  const handleOnChange = (e) => {
    const { name, value, checked } = e.target;
    setUserObject((prev) =>
      produce(prev, (draft) => {
        if (name === "rtPaceMakers" || name === "rtDefibrillators") {
          draft[name] = checked;
        } else {
          if (value === "1" && name !== "mobileNumber") draft[name] = true;
          else if (value === "0" && name !== "mobileNumber") draft[name] = false;
          else draft[name] = value;
        }
      })
    );

    setError({});
  };

  const onChangeLocation = (e, location) => {
    const { value } = e.target;
    setUserObject((prev) =>
      produce(prev, (draft) => {
        if (value === "ACTIVE")
          if (!draft["locations"]) {
            draft["locations"] = [location];
          } else {
            draft["locations"].push(location);
          }
        else {
          const findIndex = draft["locations"].findIndex((l) => l.id === location.id);
          if (findIndex !== -1) {
            draft["locations"].splice(findIndex, 1);
          }
        }
      })
    );
  };

  const onRestrictLocation = async (location, mode = "restrict") => {
    try {
      if (mode === "restrict") {
        await fetchHelper.post(`/patientmanagement/rest/picklocation`, {
          locationId: location.id,
          userId: userObject.id,
        });
        setUserObject((prev) =>
          produce(prev, (draft) => {
            draft.pickLocations.push({ id: location.id, name: location.name });
          })
        );
      } else {
        await fetchHelper.delete(
          `/patientmanagement/rest/picklocation/${location.id}?userId=${userObject.id}`
        );
        setUserObject((prev) =>
          produce(prev, (draft) => {
            const findIndex = draft.pickLocations.findIndex((l) => l.id === location.id);
            if (findIndex !== -1) draft.pickLocations.splice(findIndex, 1);
          })
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const validateFields = () => {
    const fieldsWithErrors = {};
    let isValid = true;
    if (
      (userObject.rtPaceMakers === true || userObject.rtDefibrillators === true) &&
      (userObject.userNpiNumber === "" || !Number(userObject["userNpiNumber"]))
    ) {
      fieldsWithErrors.userNpiNumber = true;
      isValid = false;
    }
    setError(fieldsWithErrors);
    return isValid;
  };

  const cleanData = () => {
    setError({});
  };

  const onEditUser = async () => {
    if (!validateFields()) {
      return;
    }
    try {
      setIsLoading(true);

      await fetchHelper.put(
        `/patientmanagement/rest/v2/users/${userObject.uuid}`,
        produce(userObject, (draft) => {
          delete draft.accountNonLocked;
          delete draft.createdAt;
          delete draft.isIrStatus;
          delete draft.isServiceReport;
          delete draft.pickLocations;
          delete draft.username;
        })
      );
      const link = document.createElement("a");
      link.href = "#";
      link.click();
      setIsLoading(false);
      await fetchData();
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const onCreateUser = async () => {
    if (!validateFields()) {
      return;
    }
    try {
      setIsLoading(true);

      await fetchHelper.post("/patientmanagement/rest/v2/users", userObject);
      const link = document.createElement("a");
      link.href = "#";
      link.click();
      setUserObject({ title: "NULL" });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const onUpdateUserStatus = async (enabled) => {
    try {
      setIsLoading(true);
      await fetchHelper.put(`/patientmanagement/rest/v2/users/${userObject.uuid}`, {
        ...userObject,
        enabled,
      });
      setUserObject((prev) =>
        produce(prev, (draft) => {
          draft["enabled"] = enabled;
        })
      );
      setIsLoading(false);
      await fetchData();
    } catch ({ validationErrors }) {
      setIsLoading(false);
      validationErrors &&
        validationErrors.forEach((err) => {
          addToast(err.errorMessage, {
            appearance: "error",
            autoDismiss: false,
          });
        });
    }
  };

  const userTitles = [
    { key: "MD", value: "MD" },
    { key: "PA", value: "PA" },
    { key: "NP", value: "NP" },
    { key: "EMR_EXPORT", value: "EMR Export" },
  ];

  return (
    <>
      {/* User Modal */}
      <div className="modal override-row create-interrogation-modal popup" id={modalId}>
        <div className="modalContent">
          <div style={{ position: "relative" }}>
            {editMode ? (
              isEditable ? null : (
                <div
                  style={{
                    opacity: 0.3,
                    position: "absolute",
                    left: 0,
                    top: 0,
                    right: 0,
                    bottom: 0,
                    background: "#fff",
                    zIndex: 30,
                  }}
                ></div>
              )
            ) : null}
            <div className="grpHdr">
              <h3>{title}</h3>
              {editMode ? (
                userObject?.enabled ? (
                  <button
                    className="btn btn-teal"
                    style={{ float: "right" }}
                    onClick={() => onUpdateUserStatus(false)}
                  >
                    Disable User
                  </button>
                ) : (
                  <div style={{ float: "right", display: "flex", alignItems: "center" }}>
                    <span style={{ marginRight: 10, fontWeight: "bold" }}>This User is disabled</span>
                    <button className="btn btn-teal" onClick={() => onUpdateUserStatus(true)}>
                      Enable
                    </button>
                  </div>
                )
              ) : null}
            </div>
            <div className="row rowStretch">
              <div className="col col-s-100 col30">
                <label>
                  <span>First Name</span>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter First Name..."
                    value={userObject["name"] || ""}
                    onChange={handleOnChangeV2}
                  />
                </label>
              </div>
              <div className="col col-s-100 col30" style={{ margin: "0 5px" }}>
                <label>
                  <span>Last Name</span>
                  <input
                    type="text"
                    name="family"
                    placeholder="Enter Last Name..."
                    value={userObject["family"] || ""}
                    onChange={handleOnChangeV2}
                  />
                </label>
              </div>
              {editMode ? (
                <div className="col col-s-100 col30">
                  <label className="inCal">
                    <span>Username</span>
                    <input type="text" name="username" value={userObject["username"] || ""} disabled />
                    <i className="ti ti-calendar" />
                  </label>
                </div>
              ) : (
                <div className="col col-s-100 col30" />
              )}
            </div>
            <div className="row rowStretch">
              <div className="col col-s-100 col30">
                <label className="inCal">
                  <span>Email Address</span>
                  <input
                    type="text"
                    name="email"
                    placeholder="Enter Email Address..."
                    value={userObject["email"] || ""}
                    onChange={handleOnChangeV2}
                  />
                  <i className="ti ti-calendar" />
                </label>
              </div>
              <div className="col col-s-100 col30" style={{ margin: "0 5px" }}>
                <label className="inCal">
                  <span>Title</span>
                  <select name="title" value={userObject["title"]} onChange={handleOnChange}>
                    <option value="NULL">Select Title...</option>
                    {userTitles.map((userTitle) => (
                      <option key={userTitle.key} value={userTitle.key}>
                        {userTitle.value}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="col col-s-100 col30">
                <label>
                  <span>Phone</span>
                  <input
                    type="text"
                    name="mobileNumber"
                    placeholder="Enter 10digit Phone Number..."
                    value={userObject["mobileNumber"] || ""}
                    onChange={handleOnChange}
                  />
                </label>
              </div>
            </div>
            <div className="row rowStretch">
              <div className="col col-s-100 col30">
                <label className="inCal">
                  <span>NPI</span>
                  <input
                    type="text"
                    name="userNpiNumber"
                    placeholder="Enter NPI..."
                    value={userObject["userNpiNumber"] || ""}
                    onChange={handleOnChangeV2}
                  />
                  {error.userNpiNumber && (
                    <span className="error">Required fields missing data. Please review and re-submit..</span>
                  )}
                  <i className="ti ti-calendar" />
                </label>
              </div>
              <div className="col col-s-100 col30" style={{ margin: "0 5px" }}>
                <label>
                  <span>External EHR ID</span>
                  <input
                    type="text"
                    name="ehrId"
                    placeholder="Enter the external ID"
                    value={userObject["ehrId"] || ""}
                    onChange={handleOnChangeV2}
                  />
                </label>
              </div>
              <div className="col col-s-100 col30"></div>
            </div>
            <div className="row rowStretch">
              <div className="col col-s-100 col50" style={{ backgroundColor: "#f3f3f3", padding: "10px" }}>
                <label>
                  <span>Interrogation</span>
                  <select
                    name="interrogation"
                    value={userObject["interrogation"] || ""}
                    onChange={handleOnChange}
                  >
                    <option value="">Select State...</option>
                    {[
                      { label: "NONE", value: "NONE" },
                      { label: "ALL", value: "ALL" },
                      { label: "Biotronik", value: "BIOTRONIK" },
                      { label: "Boston Scientific", value: "BOSTONSCI" },
                      { label: "Medtronic", value: "MEDTRONIC" },
                      { label: "St Jude", value: "STJUDE" },
                      { label: "Biotel", value: "BIOTEL" },
                    ].map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>
                <div
                  style={{
                    marginTop: "20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <span style={{ marginRight: "5px", fontSize: "14px" }}>eReportTool(eSign Button)</span>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <label className="formLi flex0" style={{ marginTop: "initial" }}>
                      <input
                        id="rtPaceMakers"
                        type="checkbox"
                        className="chk"
                        name="rtPaceMakers"
                        checked={userObject["rtPaceMakers"] || ""}
                        onChange={handleOnChange}
                      />
                      <span className="chkmrk" />
                      <span className="lbl-txt" style={{ marginTop: 1, marginLeft: -2 }}>
                        Pacemakers
                      </span>
                    </label>
                    <label className="formLi flex0" style={{ marginTop: "initial" }}>
                      <input
                        id="rtDefibrillators"
                        type="checkbox"
                        className="chk"
                        name="rtDefibrillators"
                        checked={userObject["rtDefibrillators"] || ""}
                        onChange={handleOnChange}
                      />
                      <span className="chkmrk" />
                      <span className="lbl-txt" style={{ marginTop: 1, marginLeft: -2 }}>
                        Defibrillators
                      </span>
                    </label>
                  </div>
                </div>
                <PermissionItem
                  name="prepper"
                  title="eReportTool(Device tech View)"
                  value={userObject["prepper"]}
                  onChange={handleOnChange}
                />
                <PermissionItem
                  name="readingPanelSchedule"
                  title="Reading Panel Schedule"
                  value={userObject["readingPanelSchedule"]}
                  onChange={handleOnChange}
                />
                <PermissionItem
                  name="admin"
                  title="Admin Inbox View"
                  value={userObject["admin"]}
                  onChange={handleOnChange}
                />
                <PermissionItem
                  name="clinicTriage"
                  title="Clinic Triage Role"
                  value={userObject["clinicTriage"]}
                  onChange={handleOnChange}
                />
                <PermissionItem
                  name="rcc"
                  title="Remote Care Coordinator (RCC)"
                  value={userObject["rcc"]}
                  onChange={handleOnChange}
                />
              </div>
              <div className="col col-s-100 col50">
                <label className="title-label">
                  <span>User Status</span>
                </label>
                <div
                  className="containerFlex containerFormLi"
                  style={{ flexDirection: "row", height: "380px", overflowX: "scroll" }}
                >
                  {user?.locations
                    .sort((a, b) => a.id - b.id)
                    .map((location) => (
                      <div
                        key={location.id}
                        style={{
                          marginTop: "20px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                          width: "100%",
                        }}
                      >
                        <span style={{ marginRight: "5px", fontSize: "14px", fontWeight: "bold" }}>
                          {location.name}
                        </span>
                        <div style={{ display: "flex" }}>
                          <div style={{ display: "flex", alignItems: "baseline", marginRight: 15 }}>
                            <select
                              onChange={(e) => onChangeLocation(e, location)}
                              value={
                                userObject?.locations?.some((l) => l.id === location.id)
                                  ? "ACTIVE"
                                  : "INACTIVE"
                              }
                              style={{ minWidth: 90 }}
                            >
                              <option value="ACTIVE">Active</option>
                              <option value="INACTIVE">Inactive</option>
                            </select>
                          </div>

                          {editMode ? (
                            <button
                              className={`btn ${
                                userObject?.pickLocations?.some((l) => l.id === location.id)
                                  ? "btn-teal"
                                  : "btn-light-gray"
                              }`}
                              style={{ padding: "6px", minWidth: 106 }}
                              onClick={() =>
                                onRestrictLocation(
                                  location,
                                  userObject?.pickLocations?.some((l) => l.id === location.id)
                                    ? "remove"
                                    : "restrict"
                                )
                              }
                            >
                              {userObject?.pickLocations?.some((l) => l.id === location.id) ? (
                                <>
                                  <div
                                    className="check-flat-ico icon-64"
                                    style={{ marginRight: 0, width: "17px", height: "25px" }}
                                  ></div>
                                  Restricted
                                </>
                              ) : (
                                "Restrict"
                              )}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
          <div className="row rowStretch noMargin">
            {editMode ? (
              <div className="col create-ir-col" style={{ display: "flex" }}>
                {isEditable ? (
                  <button className="btn btnSuccess" disabled={isLoading} onClick={onEditUser}>
                    {isLoading ? (
                      <div
                        id="results-loading-icon"
                        className="saving-white-ico icon-64"
                        style={{ width: 15, height: 15, marginRight: 3 }}
                      />
                    ) : (
                      <i className="ti ti-plus" />
                    )}
                    Save
                  </button>
                ) : (
                  <a href="#" onClick={() => setIsEditable(false)}>
                    <button className="btn btnSuccess">Done</button>
                  </a>
                )}
                {isEditable ? (
                  <a
                    href="#"
                    onClick={() => {
                      setIsEditable(false);
                      cleanData();
                    }}
                  >
                    <button className="btn btn-cancel">Cancel</button>
                  </a>
                ) : (
                  <button
                    className="btn btn-cancel"
                    onClick={() => setIsEditable(true)}
                    disabled={user && user.groups.findIndex((g) => g.name === "admin_group") === -1}
                  >
                    Edit Profile
                  </button>
                )}
              </div>
            ) : (
              <div className="col create-ir-col" style={{ display: "flex" }}>
                <button className="btn btnSuccess" disabled={isLoading} onClick={onCreateUser}>
                  {isLoading ? (
                    <div
                      id="results-loading-icon-2"
                      className="saving-white-ico icon-64"
                      style={{ width: 15, height: 15, marginRight: 3 }}
                    />
                  ) : (
                    <i className="ti ti-plus" />
                  )}
                  Add User
                </button>
                <a href="#">
                  <button
                    className="btn btn-cancel"
                    onClick={() => {
                      setUserObject({ title: "NULL" });
                      cleanData();
                    }}
                  >
                    Cancel
                  </button>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* User Modal */}

      <a href="#" className="close-popup"></a>
    </>
  );
};

export default UserModal;
